import { useMemo } from 'preact/hooks';

import { Message } from 'Components/message/index';
import type { MessageTemplateParams } from 'Components/message/message';
import SearchItem, { type SearchItemTemplateParams } from 'Components/searchItem/index';
import Loading from 'Components/smallComponents/loading';
import type { TemplateFunction, TemplateFunctionInvoker, TemplateResult } from 'Components/types';
import { setSort } from 'Core/actions/request';
import { useIdleEffect } from 'Core/hooks/index';
import { loadMoreFailedSelector, searchItemsSelector } from 'Core/selectors/search';
import { resultsLoadingSelector, viewSelector } from 'Core/selectors/show';
import { useDispatch, useSelector } from 'Core/store';
import { cloneSafe } from 'Utils/components';
import { safeCallback } from 'Utils/safeCallback';

import LoadMoreController, { type LoadMoreControllerTemplateParams } from './loadMoreController';
import ScrollController, { type Params as ScrollControllerTemplateParams } from './scrollController';

import './style.scss';

interface Props {
  template: TemplateFunction<SearchResultTemplateParams>;
  instantInfiniteScroll?: boolean;
  infiniteScrollDisabled?: boolean;
  updateCallback?: () => void;
}

export interface SearchResultTemplateParams {
  items: TemplateFunctionInvoker<SearchItemTemplateParams>[];
  loadMore: TemplateFunctionInvoker<LoadMoreControllerTemplateParams>;
  message: TemplateFunctionInvoker<MessageTemplateParams>;
  scrollController: TemplateFunctionInvoker<ScrollControllerTemplateParams>;
  setSort: (field: string) => void;
  view: string;
}

export default function SearchResult({
  template,
  instantInfiniteScroll,
  infiniteScrollDisabled,
  updateCallback = () => {
    /* stub */
  },
}: Props): TemplateResult {
  const dispatch = useDispatch();
  const setSortField = (field: string) => dispatch(setSort(field));

  const itemsData = useSelector(searchItemsSelector);
  const view = useSelector(viewSelector);

  const idleUpdateCallback = useMemo(
    () => safeCallback(updateCallback, 'update_callback', { component: 'SearchResult' }),
    [updateCallback],
  );

  useIdleEffect(idleUpdateCallback);

  const items = itemsData.repeaterComponents(SearchItem);

  const isLoadMoreFailed = useSelector(loadMoreFailedSelector);
  const message = (templ: TemplateFunction<MessageTemplateParams>) =>
    isLoadMoreFailed ? <Message template={templ} requestAction="add" key="message" /> : null;

  const loading = <Loading selector={resultsLoadingSelector} key="loading" />;

  const loadMore = (templ: TemplateFunction<LoadMoreControllerTemplateParams>) => {
    const loadMoreProps = {
      template: templ,
      isInstant: instantInfiniteScroll,
      isInfiniteScrollDisabled: infiniteScrollDisabled,
      isLoadMoreFailed,
    };
    return <LoadMoreController {...loadMoreProps} key="load-more" />;
  };

  const scrollController = (templ: TemplateFunction<ScrollControllerTemplateParams>) => (
    <ScrollController template={templ} key="scroll-to-top" />
  );

  const component = template.call({
    items,
    loadMore,
    message,
    scrollController,
    setSort: setSortField,
    view,
  });

  return cloneSafe(component, null, { appendedChildren: loading });
}
